import { Component, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { HelpContent, HelpContentService, HelpStep } from '../../../core/services/conf/help-content.service';

interface ResolvedStep {
    step: HelpStep;
    el: Element;
}

interface MarkerBox {
    top: number;
    left: number;
    width: number;
    height: number;
    radius: string;
    isCircle: boolean;
}

@Component({
    selector: 'app-floating-help',
    standalone: true,
    imports: [CommonModule, ButtonModule, DialogModule],
    template: `
        @if (tourActive) {
            <!-- Oscurece toda la pantalla respetando la forma de cada marca (círculo o rectángulo redondeado) -->
            <svg class="tour-overlay" [attr.width]="viewportWidth" [attr.height]="viewportHeight" (click)="deselect()">
                <defs>
                    <mask id="tour-mask">
                        <rect x="0" y="0" [attr.width]="viewportWidth" [attr.height]="viewportHeight" fill="white" />
                        @for (box of boxes; track $index) {
                            @if (box) {
                                @if (box.isCircle) {
                                    <circle [attr.cx]="box.left + box.width / 2" [attr.cy]="box.top + box.height / 2" [attr.r]="box.width / 2" fill="black" />
                                } @else {
                                    <rect [attr.x]="box.left" [attr.y]="box.top" [attr.width]="box.width" [attr.height]="box.height" rx="10" fill="black" />
                                }
                            }
                        }
                    </mask>
                </defs>
                <rect x="0" y="0" [attr.width]="viewportWidth" [attr.height]="viewportHeight" fill="rgba(0, 0, 0, 0.55)" mask="url(#tour-mask)" />
            </svg>

            <!-- Puntos resaltados (clicables) -->
            @for (marker of markers; track marker.step.selector; let i = $index) {
                @if (boxes[i]; as box) {
                    <div
                        class="tour-marker"
                        [class.tour-marker-selected]="selectedIndex === i"
                        [style.top.px]="box.top"
                        [style.left.px]="box.left"
                        [style.width.px]="box.width"
                        [style.height.px]="box.height"
                        [style.border-radius]="box.radius"
                        (click)="selectStep(i); $event.stopPropagation()"
                    >
                        <span class="tour-marker-badge">{{ i + 1 }}</span>
                    </div>
                }
            }

            <!-- Info del punto seleccionado -->
            @if (selectedStep) {
                <div class="tour-popover" [style.top.px]="popoverTop" [style.left.px]="popoverLeft" (click)="$event.stopPropagation()">
                    <div class="flex align-items-center justify-content-between mb-2">
                        <span class="text-xs font-semibold text-color-secondary">Punto {{ (selectedIndex ?? 0) + 1 }} de {{ markers.length }}</span>
                        <p-button icon="pi pi-times" [text]="true" [rounded]="true" size="small" pTooltip="Cerrar esta descripción" (onClick)="deselect()" />
                    </div>
                    <div class="font-semibold mb-1">
                        @if (selectedStep.icon) {
                            <i [class]="selectedStep.icon" [style.color]="selectedStep.color || 'var(--p-primary-color)'" class="mr-2"></i>
                        }
                        {{ selectedStep.title }}
                    </div>
                    @if (selectedStep.description) {
                        <div class="text-sm mb-2">{{ selectedStep.description }}</div>
                    }
                    @if (selectedStep.legend?.length) {
                        <table class="help-legend-table">
                            <tbody>
                                @for (item of selectedStep.legend; track item.label + item.description) {
                                    <tr>
                                        <td class="help-legend-color">
                                            @if (item.icon) {
                                                <i [class]="item.icon" [style.color]="item.color" class="help-legend-icon"></i>
                                            } @else {
                                                <span class="legend-dot" [style.background]="item.color"></span>
                                            }
                                            {{ item.label }}
                                        </td>
                                        <td>{{ item.description }}</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    }
                    <div class="flex align-items-center justify-content-end">
                        <div class="flex gap-2">
                            <p-button icon="pi pi-chevron-left" size="small" pTooltip="Anterior" [disabled]="(selectedIndex ?? 0) === 0" (onClick)="prevStep()" />
                            <p-button icon="pi pi-chevron-right" size="small" pTooltip="Siguiente" [disabled]="(selectedIndex ?? 0) >= markers.length - 1" (onClick)="nextStep()" />
                        </div>
                    </div>
                </div>
            }

            <!-- Indicación del tour -->
            <div class="tour-hint">
                <i class="pi pi-question-circle"></i>
                <span>Hacé clic en cada recuadro para ver qué hace</span>
                <span class="tour-hint-sep">|</span>
                <span><strong>ESC</strong> para salir del tour</span>
            </div>
        }

        <p-dialog [(visible)]="noDataDialog" header="Ayuda" [modal]="true" [style]="{ width: '430px' }" [draggable]="false" [resizable]="false">
            <div class="flex align-items-center gap-3">
                <i class="pi pi-info-circle text-3xl text-orange-500"></i>
                <span>{{ content?.noDataMessage }}</span>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Entendido" icon="pi pi-check" severity="success" (onClick)="noDataDialog = false" />
            </ng-template>
        </p-dialog>
    `,
    styles: [
        `
            .legend-dot {
                display: inline-block;
                width: 0.85rem;
                height: 0.85rem;
                border-radius: 50%;
                margin-top: 0.3rem;
                flex-shrink: 0;
            }

            .help-legend-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.85rem;
                margin-bottom: 0.75rem;
            }

            .help-legend-table td {
                padding: 0.35rem 0.5rem;
                border-bottom: 1px solid var(--p-content-border-color, #e5e7eb);
                vertical-align: middle;
            }

            .help-legend-table tr:last-child td {
                border-bottom: none;
            }

            .help-legend-color {
                font-weight: 600;
                width: 150px;
            }

            .help-legend-color .legend-dot {
                margin: 0 0.45rem 0 0;
                vertical-align: middle;
            }

            .help-legend-icon {
                margin-right: 0.45rem;
                width: 1rem;
                text-align: center;
            }

            .tour-overlay {
                position: fixed;
                inset: 0;
                width: 100%;
                height: 100%;
                z-index: 9990;
                cursor: default;
            }

            .tour-marker {
                position: fixed;
                z-index: 9991;
                border: 2px solid var(--p-primary-color);
                border-radius: 6px;
                cursor: pointer;
                transition:
                    top 0.2s ease,
                    left 0.2s ease,
                    width 0.2s ease,
                    height 0.2s ease,
                    box-shadow 0.2s ease;
                box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.35);
                animation: tour-pulse 1.8s ease-in-out infinite;
            }

            .tour-marker-selected {
                border-width: 3px;
                box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5);
                animation: none;
            }

            .tour-marker-badge {
                position: absolute;
                top: -12px;
                left: -12px;
                width: 22px;
                height: 22px;
                border-radius: 50%;
                background: var(--p-primary-color);
                color: #fff;
                font-size: 0.75rem;
                font-weight: 700;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
            }

            .tour-popover {
                position: fixed;
                z-index: 9992;
                width: 380px;
                max-width: calc(100vw - 16px);
                background: var(--p-content-background, var(--surface-card, #ffffff));
                border: 1px solid var(--p-content-border-color, #e5e7eb);
                border-radius: 10px;
                padding: 1rem;
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            }

            .tour-hint {
                position: fixed;
                top: 8px;
                left: 50%;
                transform: translateX(-50%);
                z-index: 9992;
                background: var(--p-content-background, var(--surface-card, #ffffff));
                border: 1px solid var(--p-content-border-color, #e5e7eb);
                border-radius: 999px;
                padding: 0.4rem 1rem;
                font-size: 0.85rem;
                display: flex;
                align-items: center;
                gap: 0.5rem;
                box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
                white-space: nowrap;
            }

            .tour-hint-sep {
                color: var(--p-content-border-color, #cbd5e1);
            }

            @keyframes tour-pulse {
                0%,
                100% {
                    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.35);
                }
                50% {
                    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.15);
                }
            }
        `
    ]
})
export class FloatingHelpComponent implements OnDestroy {
    private router = inject(Router);
    private helpContentService = inject(HelpContentService);

    content: HelpContent | null = null;
    noDataDialog = false;

    tourActive = false;
    markers: ResolvedStep[] = [];
    boxes: Array<MarkerBox | null> = [];
    selectedIndex: number | null = null;
    popoverTop = 0;
    popoverLeft = 0;

    viewportWidth = window.innerWidth;
    viewportHeight = window.innerHeight;

    private readonly popoverWidth = 380;
    private readonly popoverEstimatedHeight = 340;

    private readonly repositionHandler = () => this.reposition();
    private readonly keyHandler = (event: KeyboardEvent) => this.onKeyDown(event);
    private readonly tourRequestSubscription: Subscription;

    constructor() {
        this.updateHelp();
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.stopTour();
            this.updateHelp();
        });
        this.tourRequestSubscription = this.helpContentService.onTourRequested().subscribe(() => this.toggleTour());
    }

    ngOnDestroy(): void {
        this.removeListeners();
        this.tourRequestSubscription.unsubscribe();
    }

    /** Abre el tour si está cerrado, o lo cierra si ya está abierto (disparado desde el topbar) */
    toggleTour(): void {
        if (this.tourActive) {
            this.stopTour();
        } else {
            this.onHelpClick();
        }
    }

    get selectedStep(): HelpStep | null {
        return this.selectedIndex != null ? (this.markers[this.selectedIndex]?.step ?? null) : null;
    }

    onHelpClick(): void {
        // Algunas vistas requieren preparar la pantalla (ej: expandir el detalle de la primera orden)
        const prepare = this.content?.prepare ?? [];
        if (prepare.length > 0) {
            const el = document.querySelector(prepare[0]) as HTMLElement | null;
            if (el) {
                el.click();
                setTimeout(() => this.startTourResolved(), 500);
                return;
            }
        }
        this.startTourResolved();
    }

    private startTourResolved(): void {
        const resolved: ResolvedStep[] = [];
        for (const step of this.content?.steps ?? []) {
            for (const el of this.resolveElements(step)) {
                resolved.push({ step, el });
            }
        }

        if (resolved.length === 0) {
            // Sin elementos en pantalla (ej: tabla sin datos)
            if (this.content?.noDataMessage) {
                this.noDataDialog = true;
            }
            return;
        }

        this.markers = resolved;
        this.tourActive = true;
        this.selectedIndex = null;
        window.addEventListener('scroll', this.repositionHandler, true);
        window.addEventListener('resize', this.repositionHandler);
        document.addEventListener('keydown', this.keyHandler);

        this.reposition();

        // Llevar a la vista el primer punto del tour
        const firstEl = this.markers[0]?.el as HTMLElement | undefined;
        try {
            firstEl?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        } catch {
            firstEl?.scrollIntoView();
        }
        setTimeout(() => this.reposition(), 350);
    }

    /**
     * Resuelve los elementos de un paso.
     * Si el paso es "multiple", devuelve todos los coincidentes, limitados a la misma fila
     * de la tabla del primer elemento (para no marcar todas las filas).
     */
    private resolveElements(step: HelpStep): Element[] {
        const all = Array.from(document.querySelectorAll(step.selector));
        if (all.length === 0) return [];
        if (!step.multiple) return [all[0]];

        const firstRow = all[0].closest('tr');
        if (firstRow) {
            return all.filter((el) => el.closest('tr') === firstRow);
        }
        return all;
    }

    stopTour(): void {
        this.tourActive = false;
        this.boxes = [];
        this.selectedIndex = null;
        this.removeListeners();
    }

    deselect(): void {
        this.selectedIndex = null;
    }

    selectStep(index: number): void {
        this.selectedIndex = index;
        this.positionPopover();
    }

    nextStep(): void {
        if (this.selectedIndex == null) {
            this.selectStep(0);
            return;
        }
        if (this.selectedIndex < this.markers.length - 1) {
            this.selectStep(this.selectedIndex + 1);
            this.scrollToSelected();
        }
    }

    prevStep(): void {
        if (this.selectedIndex == null) return;
        if (this.selectedIndex > 0) {
            this.selectStep(this.selectedIndex - 1);
            this.scrollToSelected();
        }
    }

    private scrollToSelected(): void {
        const el = this.selectedIndex != null ? (this.markers[this.selectedIndex]?.el as HTMLElement | undefined) : undefined;
        if (!el) return;
        try {
            el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        } catch {
            el.scrollIntoView();
        }
        this.reposition();
        setTimeout(() => this.reposition(), 350);
    }

    private reposition(): void {
        if (!this.tourActive) return;

        this.viewportWidth = window.innerWidth;
        this.viewportHeight = window.innerHeight;

        this.boxes = this.markers.map((marker) => {
            const el = marker.el as HTMLElement;
            const rect = el.getBoundingClientRect();

            // Elementos pequeños (iconos/botones) → círculo; elementos grandes (estados, textos) → rectángulo redondeado
            const isIconSized = !marker.step.forceRect && rect.width <= 52 && rect.height <= 52;

            if (isIconSized) {
                // Cuadrado perfecto centrado en el elemento, para que círculo (marca y máscara) coincidan
                const side = Math.max(rect.width, rect.height) + 8;
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;
                return {
                    top: centerY - side / 2,
                    left: centerX - side / 2,
                    width: side,
                    height: side,
                    radius: '999px',
                    isCircle: true
                };
            }

            // Los rectángulos se agrandan para dar mejor margen
            const pad = 10;
            return {
                top: rect.top - pad,
                left: rect.left - pad,
                width: rect.width + pad * 2,
                height: rect.height + pad * 2,
                radius: '10px',
                isCircle: false
            };
        });

        this.positionPopover();
    }

    private positionPopover(): void {
        if (this.selectedIndex == null) return;
        const box = this.boxes[this.selectedIndex];
        if (!box) return;

        const spaceBelow = window.innerHeight - (box.top + box.height);
        const top = spaceBelow > this.popoverEstimatedHeight + 20 ? box.top + box.height + 12 : Math.max(8, box.top - this.popoverEstimatedHeight - 12);
        const left = Math.min(Math.max(8, box.left), Math.max(8, window.innerWidth - this.popoverWidth - 8));

        this.popoverTop = top;
        this.popoverLeft = left;
    }

    private onKeyDown(event: KeyboardEvent): void {
        if (!this.tourActive) return;
        if (event.key === 'Escape') {
            this.stopTour();
        } else if (event.key === 'ArrowRight') {
            this.nextStep();
        } else if (event.key === 'ArrowLeft') {
            this.prevStep();
        }
    }

    private removeListeners(): void {
        window.removeEventListener('scroll', this.repositionHandler, true);
        window.removeEventListener('resize', this.repositionHandler);
        document.removeEventListener('keydown', this.keyHandler);
    }

    private updateHelp(): void {
        this.content = this.helpContentService.getHelpForUrl(this.router.url);
        this.noDataDialog = false;
    }
}
